/*----------------------------------
- WATCHLISTS FEATURE PAGE CONTENT CATALOG (SSOT)
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { EDITION_PAGES } from '@uniquedomains/components/contracts/editions/public';

import type { ProductFeaturePageContent } from '@/client/catalogs/landing/product/types';
import { FULL_ACCESS_TERMS_LINE, START_WITH_SEARCH_LABEL } from '@/client/catalogs/landing/accessOffer';

/*----------------------------------
- CONTENT
----------------------------------*/

export const PRODUCT_WATCHLISTS_PAGE_CONTENT: ProductFeaturePageContent = {
    name: 'Watchlists',
    hero: {
        title: 'Keep the names that matter in a live shortlist.',
        body:
            'Track price drops, expiring names, status changes, valuation moves, risk flags, notes, tags and review state from one Watchlist, so your next session starts with what changed instead of a re-check of every name.',
        ctaLabel: 'Start a Watchlist',
        // Illustrative sample data only. Authored names, notes and prices, never a
        // real watched entry, and the whole card is rendered aria-hidden.
        mock: {
            letter: 'W',
            title: 'Energy tooling · 12 names',
            subtitle: 'Watchlist · 4 changed since yesterday',
            badge: '1 urgent',
            chips: ['Price drops', 'Ending soon', 'Status', 'Risk', 'TLD coverage'],
            rows: [
                { lead: { word: 'gridshift', tld: 'ai' }, note: 'Price drop 9%', tone: 'low', value: '$2,260' },
                { lead: { word: 'kilowatt', tld: 'co' }, note: 'Ends in 48 h', tone: 'expiring', value: '$3,900' },
                { lead: { word: 'voltage', tld: 'io' }, note: 'Now premium', tone: 'premium', value: '$12,000' },
                { lead: { word: 'surge', tld: 'com' }, note: 'Risk flagged', tone: 'high', value: '$38,000' },
            ],
        },
    },
    how: {
        title: 'Six kinds of change, tracked for you.',
        intro:
            'Names ending within 72 hours get an urgent badge. Badges reflect fresh change events when they exist, and the current state of the name otherwise.',
        steps: [
            {
                title: 'Price and status',
                body:
                    'The ask on a watched name moved down since the last check, or the name changed status: standard to premium, or into an auction.',
                tag: 'Price drops · Status',
            },
            {
                title: 'Deadlines and risk',
                body:
                    'Registration windows closing on names you follow, with the deadline attached. A name whose risk rating worsens is flagged before you commit money.',
                tag: 'Ending soon · Risk',
            },
            {
                title: 'Value and coverage',
                body:
                    'The estimated value moves by a meaningful margin, or the same word gets more or less taken across other endings.',
                tag: 'Valuation · TLD coverage',
            },
        ],
    },
    benefits: {
        title: 'Your shortlist, your way.',
        intro: 'Organise candidates the way you think about them.',
        items: [
            {
                title: 'Notes per list',
                body: 'Write down why a name is on the list. Notes stay attached to the entry.',
            },
            {
                title: 'Tags',
                body: 'Label candidates your own way: budget range, client, or anything else worth grouping by.',
            },
            { title: 'Favorites', body: 'Star the names you keep coming back to, across every list.' },
            {
                title: 'Mark reviewed',
                body: 'Clear one name or the whole list once you have looked, so unreviewed changes stand out next time.',
            },
            { title: 'Your order', body: 'Drag candidates into the order you want. The list keeps your order.' },
            {
                title: 'More than one list',
                body: 'The same domain can sit in several Watchlists, with different notes in each.',
            },
            {
                title: 'Any domain',
                body: 'Add any valid domain name, in the database or not. It is tracked like every other entry.',
            },
            { title: 'Urgent badge', body: 'Names ending within 72 hours are marked so they cannot slip past.' },
        ],
    },
    groups: {
        title: 'Alerts you control.',
        note: 'Email is the only delivery channel today. In-app badges always work.',
        items: [
            { title: 'One digest a day', items: ['At your local hour', 'Everything that changed across all lists'] },
            {
                title: 'Urgent goes out now',
                items: ['Changes that cannot wait for the digest', 'An immediate email instead'],
            },
            {
                title: 'Five toggles',
                items: ['Price drops', 'Ending soon', 'Status changes', 'Risk flags', 'TLD coverage'],
            },
            { title: 'Quiet hours', items: ['22:00 to 07:00 by default', 'Change the window or turn it off'] },
        ],
    },
    next: {
        title: 'Add a name from wherever you meet it.',
        intro: 'The same plus control appears across the product.',
        steps: [
            {
                title: 'Search results',
                body: 'The plus control on every result card adds the name to a list without leaving the search.',
            },
            // The daily editions (ADR-0074) replaced the Opportunities drop.
            {
                title: 'The daily edition',
                body: `Keep a name from ${EDITION_PAGES.investor.name} or ${EDITION_PAGES.founder.name} before the next edition replaces it.`,
            },
            { title: 'Workspace cards', body: 'Every workspace card carries the same control.' },
            {
                title: 'The full report',
                body: 'Save a name straight from its report while the evidence is in front of you.',
            },
        ],
    },
    plan: {
        title: 'Watchlists are included.',
        intro:
            'With Full Access, each Radar keeps the names you save, and you rank the best of them into Finalists. Unlimited standalone Watchlists hold the work that is not tied to one Radar.',
        card: {
            name: 'Full Access',
            tagline: 'The only plan',
            period: 'per month, cancel any time',
            bullets: [
                'Unlimited standalone Watchlists, beside your Radars',
                'The same alert delivery, with per-event toggles and quiet hours',
                'Track any valid domain name, in the database or not',
            ],
            ctaLabel: 'Get every feature',
            note: FULL_ACCESS_TERMS_LINE,
        },
    },
    finalCta: {
        title: 'Your saved names stay alive after the first pass, so your next session starts with what moved.',
        ctaLabel: START_WITH_SEARCH_LABEL,
    },
    faq: [
        {
            question: 'What changes does a Watchlist track?',
            answer:
                'Six kinds of change: price drops, ending-soon deadlines, status changes, risk flags, TLD coverage moves and valuation shifts. Names ending within 72 hours get an urgent badge. Badges reflect fresh change events when they exist, and the current state of the name otherwise.',
        },
        {
            question: 'How do alerts arrive?',
            answer:
                'By email and in-app badges. A daily digest at your local hour combines what changed across your lists, and urgent changes can send an immediate email. Five per-event toggles choose what counts, and quiet hours, 22:00 to 07:00 by default, hold everything. Email is the only delivery channel today.',
        },
        {
            question: 'Can I organise the candidates?',
            answer:
                'Yes. Each entry carries notes, tags, a favorite star and a reviewed mark. Drag entries into your own order, move them between lists, and keep the same domain in several lists with different notes in each.',
        },
        {
            question: 'Can I track a domain that is not in the database?',
            answer:
                'Yes. Add any valid domain name and it starts being tracked like every other entry, even if it never appeared in a search result.',
        },
        {
            question: 'Which plans include Watchlists?',
            answer:
                'Full Access, the one plan. Each Radar keeps the names you save from it, with alert delivery, and you rank the best of them into Finalists; unlimited standalone Watchlists hold the names you save from Browse, with the same delivery.',
        },
        {
            question: 'How is a Watchlist different from a Radar?',
            answer:
                'A Radar starts from saved criteria, keeps finding new names that match, and keeps the names you save from it. A Watchlist follows the specific names you already chose outside any Radar, such as from Browse, and reports what changed on them. Most buyers use both.',
            link: {
                label: 'See how Radars find fresh matches',
                href: '/product/radars',
            },
        },
    ],
};
